import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CircleSlashIcon } from 'lucide-react';
import { SectionIntro } from '../layout/SectionIntro';
const capabilities = [{
  title: 'Products that are genuinely complicated',
  body: 'Variants, configurable options, tiered pricing, minimums, made-to-order.'
}, {
  title: 'Checkout on your terms',
  body: 'Your payment processor, your rules for tax, shipping, and account access.'
}, {
  title: 'Wholesale and repeat buyers',
  body: 'Account pricing, reorder paths, and quote-to-order for larger customers.'
}, {
  title: 'Connected to fulfilment',
  body: 'Stock, orders, and customer records synced with the systems you already run.'
}];
const volumes = [10000, 50000, 100000, 250000];
export function EcommerceFees() {
  const [volume, setVolume] = useState(50000);
  return <section id="ecommerce" className="relative overflow-hidden bg-ink py-16 lg:py-24" data-id="element-62" data-mp="1ztv2jwllj37t">
      <div className="absolute inset-0 bp opacity-70" aria-hidden="true" data-id="element-63" data-mp="1sud9bubvlahu" />
      <div className="absolute right-0 top-0 h-[28rem] w-[28rem] rounded-full bg-forge-green/[0.08] blur-[130px]" aria-hidden="true" data-id="element-64" data-mp="1swhcga1ya5h2" />

      <div className="relative mx-auto max-w-8xl px-5 sm:px-8 lg:px-10" data-id="element-65" data-mp="1j41vng2e3mxr">
        <div className="grid items-start gap-12 lg:grid-cols-12 lg:gap-12" data-id="element-66" data-mp="1j27b42v15wu7">
          <div className="lg:col-span-6" data-id="element-67" data-mp="1f2q4h63vatng">
            <SectionIntro eyebrow="Ecommerce" title={<>
                  Sell online without a cut coming off
                  <span className="block text-forge-green" data-id="element-69" data-mp="156vgrwkse8dt">every order you fulfil.</span>
                </>} body="We build stores around how you price, package, and ship — then we get out of your margin. WebsiteForge takes 0% of your transactions, at any volume, for as long as we run the store." data-id="element-68" data-mp="1a3lsk7pk6htf" />

            <div className="mt-9 grid gap-3 sm:grid-cols-2" data-id="element-70" data-mp="2qhlshioem7go">
              {capabilities.map((c, i) => <motion.div key={c.title} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.3
            }} transition={{
              duration: 0.4,
              delay: i * 0.06
            }} className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-id="element-71" data-mp="nuyehjof8pvb" data-mpi={i}>
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-forge-cyan" data-id="element-72" data-mp="16m7nzsy6zemw">0{i + 1}</span>
                  <h3 className="mt-2.5 font-display text-[14px] font-bold leading-snug text-white" data-id="element-73" data-mp="1blaor1752yp2">{c.title}</h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-mist-300" data-id="element-74" data-mp="xkhtge90jzkw">{c.body}</p>
                </motion.div>)}
            </div>

            <Link to="/#get-started" className="group mt-8 inline-flex items-center gap-2 rounded-xl border border-forge-green/40 bg-forge-green/10 px-6 py-3.5 text-[15px] font-bold text-white transition-colors hover:bg-forge-green/20" data-id="element-75" data-mp="3ibs38hzy16bz">
              Plan my store build
              <ArrowRightIcon className="h-4 w-4 text-forge-green transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-76" data-mp="3asiv0s36bdoi" />
            </Link>
          </div>

          {/* fee ledger */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.25
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-6" data-id="element-77" data-mp="3js5xlh7n2pj5">
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-navy-900/85 shadow-lift" data-id="element-78" data-mp="2r1xbxfdw9mzt">
              <div className="absolute inset-0 bp-fine opacity-70" aria-hidden="true" data-id="element-79" data-mp="3126av0z9tmiw" />

              <div className="relative flex items-center justify-between border-b border-white/10 px-6 py-4" data-id="element-80" data-mp="310d3tkkqx0ad">
                <p className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-forge-green" data-id="element-81" data-mp="28dijcnnxi0ww">Where the money goes</p>
                <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-mist-400" data-id="element-82" data-mp="1bmngv0ro8awb">Illustrative</span>
              </div>

              <div className="relative px-6 py-6" data-id="element-83" data-mp="1glqalp27auoz">
                <p className="text-[12.5px] font-semibold text-mist-200" data-id="element-84" data-mp="1kn22k18jrs0c">Monthly online sales</p>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-85" data-mp="1pda867v3qyid">
                  {volumes.map(v => <button key={v} type="button" onClick={() => setVolume(v)} aria-pressed={volume === v} className={`rounded-lg border px-3.5 py-2 font-mono text-[12px] transition-colors ${volume === v ? 'border-forge-green/50 bg-forge-green/15 text-white' : 'border-white/12 bg-white/[0.03] text-mist-300 hover:border-white/25'}`} data-id="element-86" data-mp="1ud4wq4renc09">
                      ${v.toLocaleString()}
                    </button>)}
                </div>

                <div className="mt-7 space-y-4" data-id="element-87" data-mp="1o48jna9f6dn">
                  <LedgerLine label="Stays with your business" value={`$${volume.toLocaleString()}`} pct={100} tone="green" note="Before your processor's own published rate, which we never mark up." data-id="element-88" data-mp="1ncdqeohlcof" />
                  <LedgerLine label="Transaction fees from WebsiteForge" value="$0" pct={0} tone="zero" note="No per-order cut, no revenue share, no percentage that scales with you." data-id="element-89" data-mp="pcx77ri9nsby" />
                </div>

                <div className="mt-7 flex items-start gap-4 rounded-xl bg-navy-950/70 p-5 ring-1 ring-inset ring-white/[0.08]" data-id="element-90" data-mp="3139b17f76hht">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-forge-green/15" data-id="element-91" data-mp="2w3wpcq02bggt">
                    <CircleSlashIcon className="h-5 w-5 text-forge-green" aria-hidden="true" data-id="element-92" data-mp="2xmd6m0dm6coh" />
                  </span>
                  <div data-id="element-93" data-mp="32jwiwuqr4fds">
                    <p className="font-display text-[2rem] font-extrabold leading-none tracking-[-0.02em] text-forge-green" data-id="element-94" data-mp="32iby3wu8oat4">
                      0%
                    </p>
                    <p className="mt-2 text-[13px] leading-relaxed text-mist-200" data-id="element-95" data-mp="2skns2ar6ngvn">
                      Transaction fees from WebsiteForge on the stores we build. Growing order volume costs you nothing extra
                      on our side — that is the whole point of owning the build.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
type LedgerLineProps = {
  label: string;
  value: string;
  pct: number;
  tone: 'green' | 'zero';
  note: string;
};
function LedgerLine({
  label,
  value,
  pct,
  tone,
  note
}: LedgerLineProps) {
  const green = tone === 'green';
  return <div data-id="element-96" data-mp="1ha2dj8h56gg5">
      <div className="flex items-baseline justify-between gap-4" data-id="element-97" data-mp="1h9sts2mox5iw">
        <p className="text-[13px] font-semibold text-white" data-id="element-98" data-mp="1cazimguvlqlw">{label}</p>
        <p className={`font-display text-[15px] font-extrabold ${green ? 'text-forge-green' : 'text-mist-200'}`} data-id="element-99" data-mp="1q9z6hmake1h0">{value}</p>
      </div>
      <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-white/[0.06]" data-id="element-100" data-mp="1m7avb7ydoigv">
        {pct > 0 ? <motion.div key={`${label}-${pct}`} initial={{
        width: 0
      }} animate={{
        width: `${pct}%`
      }} transition={{
        duration: 0.7,
        ease: 'easeOut'
      }} className="h-full rounded-full bg-forge-green" data-id="element-101" data-mp="2073ln25zswob" /> : <div className="h-full w-full hatch opacity-40" data-id="element-102" data-mp="24yljvigmsj5l" />}
      </div>
      <p className="mt-1.5 text-[11.5px] leading-snug text-mist-400" data-id="element-103" data-mp="2iyd5o5de33u5">{note}</p>
    </div>;
}